import { shadows } from '@airship-platform/jam-design-tokens';
import { ThemeOptions } from '@mui/material/styles';
import type {} from '@mui/x-charts/themeAugmentation';
import '../base/components'; // side-effect: keep the MUI augmentation reachable in declaration output
import { baseComponents } from '../base/components';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white?: true;
  }
}

declare module '@mui/material/Radio' {
  interface RadioPropsColorOverrides {
    blue?: true;
  }
}

declare module '@mui/material/Tabs' {
  interface TabsPropsColorOverrides {
    gold?: true;
  }
}

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    gold?: true;
    softGold?: true;
  }
}

declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    secondary?: true;
    tertiary?: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    red?: true;
    gray?: true;
    blue?: true;
    green?: true;
    gold?: true;
    softGreen?: true;
    softBlue?: true;
    softGold?: true;
    softGray?: true;
    softRed?: true;
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    gray?: true;
    blue?: true;
    green?: true;
    gold?: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body4: true;
  }
}

declare module '@mui/material/Link' {
  interface LinkPropsColorOverrides {
    tertiary?: true;
  }
}

export const platformComponents: ThemeOptions['components'] = {
  MuiBarChart: {
    defaultProps: {
      sx: { '& rect': { cursor: 'pointer' } },
    },
  },
  ...baseComponents,
  MuiLinearProgress: {
    defaultProps: {
      color: 'softGold',
    },
    styleOverrides: {
      root: {
        borderRadius: '0.375rem',
        height: '0.375rem',
      },
    },
    variants: [
      {
        props: { color: 'softGold' },
        style: ({ theme }) => ({
          '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.softGold?.[700],
          },
        }),
      },
    ],
  },
  MuiCard: {
    ...baseComponents?.MuiCard,
    variants: [
      ...(baseComponents?.MuiCard?.variants ?? []),
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.softGold?.[50],
          borderColor: theme.palette.softGold?.[300],
          borderWidth: '1px',
        }),
      },
    ],
  },
  MuiTable: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiTable-root': {
          border: 'none',
        },
        '& .MuiTableHead-root': {
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 500,
          backgroundColor: 'transparent',
        },
        '& .MuiTableCell-root': {
          fontSize: theme.typography.caption.fontSize,
          padding: '0.75rem 1rem',
          backgroundColor: 'transparent',
          borderRight: `1px solid ${theme.palette.grey[400]}`,
          borderBottom: `1px solid ${theme.palette.grey[400]}`,
        },
        '& .MuiTableCell-root:last-of-type': {
          borderRight: 'none',
        },
        '& .MuiTableBody-root .MuiTableRow-root': {
          backgroundColor: 'transparent',
        },
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme }) => ({
        'p, span, & .MuiSelect-select': {
          fontSize: theme.typography.caption.fontSize,
          fontFamily: theme.typography.caption.fontFamily,
        },
        '& .MuiTablePagination-actions': {
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          '& button': {
            boxShadow: 'none',
            fontSize: theme.typography.caption.fontSize,
          },
        },
      }),
    },
  },
  MuiButton: {
    ...baseComponents?.MuiButton,
    variants: [
      ...(baseComponents?.MuiButton?.variants ?? []),
      {
        props: { variant: 'outlined', color: 'gold' },
        style: ({ theme }) => ({
          borderColor: theme.palette.gold?.[500],
          color: theme.palette.gold?.[700],
          '&:hover': {
            borderColor: theme.palette.gold?.[600],
            backgroundColor: theme.palette.gold?.[50],
          },
        }),
      },
      {
        props: { color: 'white' },
        style: {
          boxShadow: shadows.sm,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    ],
  },
  MuiButtonGroup: {
    defaultProps: {
      size: 'medium',
      color: 'primary',
      variant: 'contained',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '3rem',
        boxShadow: 'none',
        border: 'none',
      },
    },
    variants: [
      {
        props: { variant: 'contained', color: 'info' },
        style: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
          fontWeight: theme.typography.body2.fontWeight,
          backgroundColor: theme.palette.gray?.[100],
          color: theme.palette.gray?.[800],
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',

          '.MuiButtonBase-root': {
            color: theme.palette.gray?.[800],
            border: 'none',
            margin: '4px 8px',
            padding: '0 24px',
            borderRadius: '3rem',

            '&.MuiButton-contained': {
              backgroundColor: theme.palette.common.white,
            },
          },
        }),
      },
    ],
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: theme.palette.common.white,
        '& .MuiDataGrid-columnHeader': {
          backgroundColor: theme.palette.softGold?.[50],
          color: theme.palette.text.primary,
          fontWeight: 500,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiDataGrid-cell': {
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          fontSize: theme.typography.caption.fontSize,
        },
        '& .MuiDataGrid-row': {
          '&:hover': {
            backgroundColor: theme.palette.softBlue?.[50],
          },
          '&:nth-of-type(even)': {
            backgroundColor: theme.palette.grey[50],
          },
        },
        '& .MuiDataGrid-rowReorderCell': {
          color: theme.palette.grey[500],
          '&:hover': {
            color: theme.palette.blue?.[500],
          },
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: `1px solid ${theme.palette.grey[300]}`,
        },
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .wrap-text': {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: '1.5',
          padding: '8px',
        },
      }),
    },
  },
  MuiTimeline: {
    styleOverrides: {
      root: {
        '& .MuiTimelineItem-root:before': {
          flex: 0,
          padding: 0,
        },
      },
    },
  },
  MuiDrawer: {
    defaultProps: {
      variant: 'persistent',
      slotProps: {
        paper: {
          sx: { backgroundColor: ['white', 'transparent'] },
        },
      },
    },
    variants: [
      {
        props: { navbar: 'default' },
        style: {
          '& .MuiPaper-root': {
            padding: '1.5rem',
            border: 'none',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            textOverflow: 'ellipsis',
            overflowX: 'hidden',
            justifyContent: 'space-between',
            height: '100%',
            width: '15rem',
            gap: '3rem',
          },
          minWidth: '13.125rem',
        },
      },
    ],
  },
};
